import * as translator from "../../../utils/translator";
import { ThankyouDocuments, getOpenTasks } from "./useThankyouDocuments";
import checkBadge from "../../../icons/ThankyouUkWeightLoss/check-badge.svg";
import pendingBadge from "../../../icons/ThankyouUkWeightLoss/pending-badge.svg";
import backgroundSwoosh from "../../../icons/ThankyouUkWeightLoss/background-swoosh.svg";
import infoIcon from "../../../icons/ThankyouUkWeightLoss/info.svg";
import bodyScanIcon from "../../../icons/ThankyouUkWeightLoss/body-scan.svg";
import idDocumentIcon from "../../../icons/ThankyouUkWeightLoss/id-document.svg";
import stepDoneIcon from "../../../icons/ThankyouUkWeightLoss/step-done.svg";
import stepCurrentIcon from "../../../icons/ThankyouUkWeightLoss/step-current.svg";
import stepArrow from "../../../icons/ThankyouUkWeightLoss/step-arrow.svg";
import divider from "../../../icons/ThankyouUkWeightLoss/divider.svg";
import backgroundSwooshLg from "../../../icons/ThankyouUkWeightLoss/background-swoosh-lg.svg";
import pendingClock from "../../../icons/ThankyouUkWeightLoss/pending-clock.svg";
import infoIconLg from "../../../icons/ThankyouUkWeightLoss/info-lg.svg";
import bodyScanIconLg from "../../../icons/ThankyouUkWeightLoss/body-scan-lg.svg";
import idDocumentIconLg from "../../../icons/ThankyouUkWeightLoss/id-document-lg.svg";
import proofOfUseIconLg from "../../../icons/ThankyouUkWeightLoss/proof-of-use-lg.svg";
import stepArrowLong1 from "../../../icons/ThankyouUkWeightLoss/step-arrow-long-1.svg";
import stepArrowLong2 from "../../../icons/ThankyouUkWeightLoss/step-arrow-long-2.svg";
import dividerLg from "../../../icons/ThankyouUkWeightLoss/divider-lg.svg";
import "./ThankyouUkWeightLoss.css";

interface Props {
    language?: string;
    country?: string;
    hashID?: string;
    documents: ThankyouDocuments;
    onUploadClick: () => void;
}

const TASK_ICONS = {
    photos: bodyScanIcon,
    id: idDocumentIcon,
    proof: idDocumentIcon,
};

// HL-12997: the pending page's desktop frame (Figma 9831:56861) has its own icon sizes and a proof-of-use icon.
const TASK_ICONS_DESKTOP = {
    photos: bodyScanIconLg,
    id: idDocumentIconLg,
    proof: proofOfUseIconLg,
};

const STEP_ARROWS_DESKTOP = [stepArrowLong1, stepArrowLong2];

// Keep in sync with the desktop breakpoint of .ty-ukwl--pending in ThankyouUkWeightLoss.css.
const PENDING_DESKTOP_MEDIA = "(min-width: 1200px)";

const STEPS = [
    { key: "step1", icon: stepDoneIcon },
    { key: "step2", icon: stepCurrentIcon },
    { key: "step3", icon: stepCurrentIcon },
];

// HL-12639: thank-you page for UK weight-loss orders, V3 design. HL-12997: while a verification task is open the
// page says the order is pending and lists only the open tasks; a failed task check shows generic copy pointing to
// My Account; nothing open keeps the confirmation copy.
export default function ThankyouUkWeightLoss({ language, country, hashID, documents, onUploadClick }: Props) {
    const t = (key: string) => translator.translate(language, "thankyou", `uk_wl/${key}`);

    const openTasks = getOpenTasks(documents);
    const isPending = !documents.failed && openTasks.length > 0;
    const hasCard = isPending || documents.failed;
    const cardTitle = documents.failed
        ? t("card_title_generic")
        : (openTasks.length === 1 ? t("card_title_one") : t("card_title_other")).replace(":n", String(openTasks.length));

    return (
        <>
            {/* No site header: the Figma frames start with the check badge. */}
            <div className={`ty-ukwl${isPending ? " ty-ukwl--pending" : ""}`}>
                <img src={backgroundSwoosh} alt="" className="ty-ukwl__swoosh ty-ukwl__swoosh--top" />
                <img src={backgroundSwoosh} alt="" className="ty-ukwl__swoosh ty-ukwl__swoosh--bottom" />
                {isPending && [1, 2].map((swoosh) => (
                    <div key={swoosh} className={`ty-ukwl__swoosh-lg ty-ukwl__swoosh-lg--${swoosh}`}>
                        <div className="ty-ukwl__swoosh-lg-box">
                            <img src={backgroundSwooshLg} alt="" className="ty-ukwl__swoosh-lg-img" />
                        </div>
                    </div>
                ))}

                <div className="ty-ukwl__content">
                    <div className="ty-ukwl__badge">
                        <img src={hasCard ? pendingBadge : checkBadge} alt="" className="ty-ukwl__badge-img" />
                        {isPending && (
                            <div className="ty-ukwl__badge-clock">
                                <img src={pendingClock} alt="" className="ty-ukwl__badge-clock-icon" />
                            </div>
                        )}
                    </div>

                    <div className="ty-ukwl__hero">
                        {isPending ? (
                            <h1 className="ty-ukwl__title">
                                {t("pending_title")}
                                <span className="ty-ukwl__title-rest ty-ukwl__title-rest--line">{t("pending_title_rest")}</span>
                            </h1>
                        ) : documents.failed ? (
                            <h1 className="ty-ukwl__title">{t("generic_title")}</h1>
                        ) : (
                            /* Mobile uses the short title (Figma). */
                            <h1 className="ty-ukwl__title ty-ukwl__title--short-on-mobile">
                                {t("title")}
                                <span className="ty-ukwl__title-rest">{t("title_rest")}</span>
                            </h1>
                        )}
                        <p className="ty-ukwl__subtitle">{isPending ? t("pending_subtitle") : t("subtitle")}</p>
                    </div>

                    {hasCard && (
                        <div className="ty-ukwl__card" data-testid="ty-ukwl-documents-card">
                            <div className="ty-ukwl__card-header">
                                <picture className="ty-ukwl__card-title-icon">
                                    {isPending && <source media={PENDING_DESKTOP_MEDIA} srcSet={infoIconLg} />}
                                    <img src={infoIcon} alt="" />
                                </picture>
                                <p className="ty-ukwl__card-title">{cardTitle}</p>
                                <p className="ty-ukwl__card-text">{documents.failed ? t("card_text_generic") : t("card_deadline")}</p>
                            </div>

                            {isPending && (
                                <div className="ty-ukwl__items">
                                    {openTasks.map((task, index) => (
                                        <div className={`ty-ukwl__item ty-ukwl__item--${task}`} key={task}>
                                            <div className="ty-ukwl__item-icon-box">
                                                <picture className="ty-ukwl__item-icon">
                                                    <source media={PENDING_DESKTOP_MEDIA} srcSet={TASK_ICONS_DESKTOP[task]} />
                                                    <img src={TASK_ICONS[task]} alt="" />
                                                </picture>
                                            </div>
                                            <div className="ty-ukwl__item-text">
                                                <p className="ty-ukwl__item-title">{index + 1}. {t(`${task}_item_title`)}</p>
                                                <p className="ty-ukwl__item-desc">{t(`${task}_item_desc`)}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}

                            <a
                                href={`/${country}/account/my-orders/${hashID}/details`}
                                className="ty-ukwl__upload-btn"
                                onClick={onUploadClick}
                            >
                                {documents.failed ? t("generic_btn") : t("complete_btn")}
                                <span className="ty-ukwl__upload-btn-arrow"> →</span>
                            </a>
                        </div>
                    )}

                    <div className="ty-ukwl__steps">
                        <h2 className="ty-ukwl__steps-title">{t("steps_title")}</h2>
                        <div className="ty-ukwl__timeline">
                            {STEPS.map((step, index) => (
                                <div key={step.key} className="ty-ukwl__timeline-item">
                                    {index > 0 && (
                                        <div className="ty-ukwl__step-connector">
                                            <img src={stepArrow} alt="" className="ty-ukwl__step-arrow" />
                                            {isPending && (
                                                <img
                                                    src={STEP_ARROWS_DESKTOP[index - 1]}
                                                    alt=""
                                                    className={`ty-ukwl__step-arrow-lg ty-ukwl__step-arrow-lg--${index}`}
                                                />
                                            )}
                                        </div>
                                    )}
                                    <div className={`ty-ukwl__step${index === 0 ? " ty-ukwl__step--first" : ""}`}>
                                        <img src={step.icon} alt="" className="ty-ukwl__step-icon" />
                                        <div className="ty-ukwl__step-text">
                                            <p className="ty-ukwl__step-title">{t(`${step.key}_title`)}</p>
                                            <p className="ty-ukwl__step-desc">{t(`${step.key}_desc`)}</p>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <picture className="ty-ukwl__divider">
                        {isPending && <source media={PENDING_DESKTOP_MEDIA} srcSet={dividerLg} />}
                        <img src={divider} alt="" />
                    </picture>
                    <p className="ty-ukwl__footer">{t("footer")}</p>
                </div>
            </div>
        </>
    );
}
